import React from 'react';
import Resume from "../files/MayurResume.pdf";
import { motion } from 'framer-motion';
import { TypeAnimation } from 'react-type-animation';

// Pulled from the same stack described in the bio, surfaced as chips so the
// hero can be scanned instead of read as a wall of prose.
const focusAreas = [
  "LangGraph",
  "FastAPI",
  "RAG",
  "Pinecone",
  "Chroma",
  "Ollama",
  "Mistral",
  "Groq",
  "Gemini",
];

const socials = [
  {
    label: "GitHub",
    href: "https://github.com/Mayuradlak123",
    path: "M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z",
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/mayur-adlak-31b962220/",
    path: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z",
  },
];

function Hero() {
  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = Resume;
    link.download = "Mayur-Resume";
    link.target = "_blank";
    link.click();
  };

  return (
    <section
      className="relative flex min-h-screen flex-col justify-center overflow-hidden px-4 py-24 sm:px-6 lg:px-8"
      id="home"
    >
      {/* Animated background elements */}
      <div className="absolute left-10 top-20 h-64 w-64 animate-blob-slow rounded-full bg-blue-300 opacity-20 mix-blend-multiply blur-xl"></div>
      <div className="absolute bottom-32 right-10 h-72 w-72 animate-blob-medium rounded-full bg-indigo-300 opacity-20 mix-blend-multiply blur-xl"></div>
      <div className="absolute left-1/3 top-1/2 h-56 w-56 animate-blob-fast rounded-full bg-purple-300 opacity-20 mix-blend-multiply blur-xl"></div>

      <div className="relative z-10 mx-auto w-full max-w-4xl text-center">
        {/* Current role, sourced from the experience data */}
        <motion.div
          className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-slate-200 bg-white/70 py-1.5 pl-2 pr-4 shadow-sm backdrop-blur"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75 motion-reduce:animate-none"></span>
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
          </span>
          <span className="text-[11px] tracking-wide text-slate-600">
            Software Engineer @ Pure Billion Technologies
          </span>
        </motion.div>

        {/* The name is fixed; only the role rotates */}
        <motion.h1
          className="mb-4 bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-5xl font-extrabold tracking-tight text-transparent md:text-7xl"
          initial={{ opacity: 0, y: -24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          Mayur Adlak
        </motion.h1>

        <motion.p
          className="mb-8 flex min-h-[2.5rem] items-center justify-center text-xl font-bold text-slate-700 md:min-h-[3rem] md:text-3xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <TypeAnimation
            sequence={[
              'Agentic AI Engineer',
              1600,
              'Full-Stack Developer',
              1600,
              'GenAI Product Builder',
              1600,
            ]}
            wrapper="span"
            speed={50}
            repeat={Infinity}
            aria-label="Agentic AI Engineer, Full-Stack Developer, GenAI Product Builder"
          />
        </motion.p>

        {/* Lead paragraph, held near 65 characters per line */}
        <motion.p
          className="mx-auto mb-8 max-w-2xl text-justify text-base leading-relaxed text-slate-600 [hyphens:auto] md:text-lg"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          I build intelligent systems that turn raw data into conversational insight: stateful,
          multi-agent workflows served over high-performance backends. Currently shipping GenAI
          products: an AI-powered HR assistant, a natural-language query layer for complex database
          schemas, an engineering intelligence platform, and{" "}
          <span className="font-semibold text-slate-800">langguard-core</span>, a guardrails
          framework for LLM applications.
        </motion.p>

        {/* Focus areas */}
        <motion.ul
          className="mx-auto mb-10 flex max-w-2xl flex-wrap justify-center gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          {focusAreas.map((area) => (
            <li
              key={area}
              className="rounded-full border border-slate-200 bg-white/70 px-3 py-1 font-mono text-[11px] tracking-wide text-slate-600 backdrop-blur transition-colors duration-300 hover:border-indigo-300 hover:text-indigo-700"
            >
              {area}
            </li>
          ))}
        </motion.ul>

        {/* Actions */}
        <motion.div
          className="flex flex-col items-center justify-center gap-3 sm:flex-row"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <button
            type="button"
            onClick={handleDownload}
            className="btn-hover-effect group inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-3 font-semibold text-white shadow-md transition-shadow duration-300 hover:shadow-lg hover:shadow-indigo-500/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 sm:w-auto"
          >
            Download Resume
            <svg
              className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
            </svg>
          </button>

          <a
            href="#projects"
            className="group inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white/70 px-6 py-3 font-semibold text-slate-700 shadow-sm backdrop-blur transition-colors duration-300 hover:border-indigo-300 hover:text-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 sm:w-auto"
          >
            View Projects
            <svg
              className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7-7 7M3 12h18" />
            </svg>
          </a>
        </motion.div>

        {/* Social links */}
        <motion.div
          className="mt-10 flex items-center justify-center gap-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1 }}
        >
          {socials.map((social) => (
            <a
              key={social.label}
              href={social.href}
              target="_blank"
              rel="noreferrer"
              aria-label={`${social.label} profile, opens in a new tab`}
              className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white/70 text-slate-500 shadow-sm backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-indigo-300 hover:text-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" clipRule="evenodd" d={social.path} />
              </svg>
            </a>
          ))}
          <a
            href="mailto:mayuradlak030@gmail.com"
            aria-label="Email Mayur Adlak"
            className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white/70 text-slate-500 shadow-sm backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-indigo-300 hover:text-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </a>
        </motion.div>
      </div>

      {/* Scroll Down Indicator */}
      <motion.a
        href="#about"
        aria-label="Scroll to about section"
        className="absolute bottom-8 left-1/2 text-blue-600/70 transition-colors hover:text-blue-700"
        style={{ x: "-50%" }}
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
      >
        <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </motion.a>
    </section>
  );
}

export default Hero;
